import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import React from 'react';
import { useMultiWindowContext } from '../../../app/components/multi-window/context/multi-window-context';
import { useAppDispatch } from '../../../app/hooks/store-hooks';
import { NewRouteModel, routeAnalyzerAddRoutes } from '../../../app/state/slices/route-analyzer-slice';
import routeApi from '../../../services/http/route-api';
import { AnalyzerRouteType } from '../types';
import { ImportRoutesProps } from './FpmImportRoutes';

export function FileImportRoutes(props: ImportRoutesProps) {
    const multiWindowContext = useMultiWindowContext();
    const [origin, setOrigin] = React.useState('');
    const [destination, setDestination] = React.useState('');
    const [file, setFile] = React.useState<File | null>(null);
    const dispatch = useAppDispatch();

    const handleOriginChanged = React.useCallback((event: TextBoxChangeEvent) => setOrigin(event.value as string), []);
    const handleDestinationChanged = React.useCallback((event: TextBoxChangeEvent) => setDestination(event.value as string), []);
    const handleFileChanged = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            setFile(event.target.files[0]);
        }
    }, []);

    const handleImport = async () => {
        if (!file) {
            return;
        }

        let routes = await importRoutes(file);
        if (origin) {
            routes = routes.filter((x) => x.origin === origin);
        }

        if (destination) {
            routes = routes.filter((x) => x.destination === destination);
        }

        if (routes.length < 20) {
            const decoded = await routeApi.decodeRoute(...routes.map((x) => x.route));
            if (decoded) {
                for (let i = 0; i < decoded.routes.length; i++) {
                    routes[i].waypoints = {
                        items: decoded.routes[i]?.wp ?? null
                    };
                }
            }
        }

        dispatch(routeAnalyzerAddRoutes({ routes }));
        props.onClose();
    };

    const handleCancel = () => {
        props.onClose();
    };

    return (
        <Dialog width={400} title={'Import from file'} onClose={handleCancel} appendTo={multiWindowContext.root}>
            <div className="d-flex gap-2 flex-column pb-2">
                <TextBox
                    className="input-uppercase"
                    placeholder="Origin (optional)"
                    value={origin}
                    onChange={handleOriginChanged}></TextBox>
                <TextBox
                    className="input-uppercase"
                    placeholder="Destination (optional)"
                    value={destination}
                    onChange={handleDestinationChanged}></TextBox>
                <TextBox type="file" accept=".csv" multiple={false} onChange={(e) => handleFileChanged(e.syntheticEvent)} />
            </div>
            <DialogActionsBar>
                <div className="d-flex gap-2">
                    <Button className="flex-grow-1" themeColor={'primary'} onClick={handleImport} disabled={!file}>
                        Import
                    </Button>
                    <Button className="flex-grow-1" fillMode={'outline'} onClick={handleCancel}>
                        Cancel
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
}

export async function importRoutes(file: File): Promise<NewRouteModel[]> {
    const lines = await file.text();

    const result: NewRouteModel[] = [];
    const parseType = (type: string): AnalyzerRouteType => {
        if (type in AnalyzerRouteType) {
            return type as AnalyzerRouteType;
        }
        return AnalyzerRouteType.other;
    };

    for (const line of lines.split('\n')) {
        const split = line.split(',').map((x) => x.trim());
        if (split.length < 5) {
            continue;
        }
        result.push({
            type: parseType(split[0]),
            label: split[5] ?? `${split[1]}-${split[2]}-${split[3]}`,
            origin: split[1],
            destination: split[2],
            sequence: split[3],
            distance: null,
            ete: null,
            time: null,
            route: split[4],
            waypoints: null,
            associatedPoints: [],
            feedId: null,
            visible: false
        });
    }

    return result;
}
